import { reactive, watchEffect } from "vue";

export const useMenu = () => {
    const { t } = useI18n();
    const { data: categories } = useCategories();
    const switchLocalePath = useSwitchLocalePath();
    const route = useRoute();

    const menu = reactive<Menu>({
        userMenu: {
            icon: "majesticons:scan-user",
            list: [],
        },
        navigationMenu: {
            icon: "majesticons:menu",
            list: [],
        },
        langSwitcher: {
            title: "",
            icon: "icon-park-outline:translate",
            href: route.path,
            subItems: [],
        },
    });

    const buildMenu = () => {
        const cats = useFilter(categories.value?.data, (e: APICategory) => e.parent === undefined) ?? [];
        const currentPath = route.path;

        menu.userMenu.list = [
            {
                title: t("general.profile"),
                icon: "majesticons:user-box",
                href: "/profile",
            },
            {
                title: t("general.settings"),
                icon: "majesticons:settings-cog",
                href: "/settings",
            },
            { type: "divider" },
            {
                title: t("general.logout"),
                icon: "majesticons:logout",
                href: "/logout",
            },
        ];

        menu.navigationMenu.list = [
            {
                title: t("general.home"),
                icon: "majesticons:home",
                href: "/",
            },
            {
                title: t("general.verify"),
                icon: "majesticons:barcode-2-line",
                href: "/verify",
            },
            {
                title: t("general.categories"),
                icon: "majesticons:tickets-line",
                href: "/categories",
                class: "col-span-2",
                subItems: cats.map((item: APICategory) => ({
                    title: t(`category.name.${item.name}`, item.name),
                    image: item.thumb,
                    href: `/categories/${item.slug}`,
                })),
            },
            {
                title: t("general.about.label"),
                icon: "majesticons:info-circle",
                href: "/about",
            },
            {
                title: t("general.contact.label"),
                icon: "majesticons:phone-dial",
                href: "/contact",
            },
        ];

        menu.langSwitcher.title = t("general.select_language");
        menu.langSwitcher.href = currentPath;
        menu.langSwitcher.subItems = [
            {
                title: t("general.languages.en"),
                icon: "flag:gb-4x3",
                href: switchLocalePath("en"),
            },
            {
                title: t("general.languages.fa"),
                icon: "flag:ir-4x3",
                href: switchLocalePath("fa"),
            },
        ];
    };

    watchEffect(buildMenu);

    return menu;
};

export interface NavMenuItem {
    title: string;
    href: string;
    icon?: string;
    image?: string;
    description?: string;
    class?: string;
    subItems?: NavMenuItem[];
}

interface BaseMenuItem {
    class?: string; // optional styling on both
}

/**
 * Divider
 * Example: { type: "divider", class: "my-2" }
 */
interface DividerMenuItem extends BaseMenuItem {
    type: "divider";
}

/**
 * Navigational item (default)
 * Example: { title: "Profile", href: "/profile", icon: "user" }
 */
interface ActionMenuItem extends BaseMenuItem {
    type?: "item"; // default if omitted
    title: string;
    href: string;
    icon?: string;
    class?: string;
    action?: () => void;
}

export type UserMenuItem = DividerMenuItem | ActionMenuItem;

export interface MenuItem<T> {
    icon?: string;
    list: T[];
}

export interface Menu {
    userMenu: MenuItem<UserMenuItem>;
    navigationMenu: MenuItem<NavMenuItem>;
    langSwitcher: NavMenuItem;
}
